/* eslint-disable no-unused-expressions */
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { format } from "date-fns";
import { Loading } from "@networkraildigitalfactory/components";
import TableView from "../TableView/TableView";
import Filters from "../Filters/Filters";
import Pagination from "../Pagination/PaginationPage";
import styles from "../MyObjectives/MyObjectives.module.css";
import { useObjectives, usePDP } from "../../hooks/useAPI";
import { AreYouSureModal } from "../Modals";

const ObjectivesContent = (props) => {
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [objectives, setObjectives] = useState();
  const history = useHistory();
  const {
    isLineManagerView,
    setObjectivesFilters,
    objectivesFilters,
    displayAccessDeniedModal,
    employeeEmail,
    type,
  } = props;
  const actionFor = type === "PDP" ? "development-goal" : "objective";
  const url = isLineManagerView
    ? `adminPaginated/${objectivesFilters.page}`
    : `paginated/${objectivesFilters.page}`;
  const [{ loading: objectivesLoading, error: postObjectiveError }, executeObjectivePost] =
    useObjectives(
      {
        method: "POST",
      },
      { manual: true }
    );

  const [{ loading: pdpsLoading, error: postPdpError }, executePdpPost] = usePDP(
    {
      method: "POST",
    },
    { manual: true }
  );

  // pass error object up to HoC
  if (isLineManagerView) {
    displayAccessDeniedModal(postObjectiveError || postPdpError);
  }

  const [isDeleteDraftModalOpen, setIsDeleteDraftModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState();

  const openDeleteDraftModal = (Id) => {
    setSelectedItemId(Id);
    setIsDeleteDraftModalOpen(true);
  };

  const closeDeleteDraftModal = () => {
    setIsDeleteDraftModalOpen(false);
  };

  const fetchData = async ({ dueDate, status, limit }) => {
    try {
      setLoading(true);
      const dataToSend = {
        employeeEmail,
        dueDate: dueDate || "",
        status: status || "",
        limit: limit || objectivesFilters.pageSize,
        sort: "-1",
      };

      let result;
      if (type === "PDP") {
        result = await executePdpPost({
          url,
          data: dataToSend,
        });
      } else {
        result = await executeObjectivePost({
          url,
          data: dataToSend,
        });
      }
      setObjectives(result?.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const onPageChange = async (page, _pageSize) => {
    setObjectivesFilters("page", page);
    fetchData({ page, limit: _pageSize });
  };

  const onPageSizeChange = (currentPage, size) => {
    setObjectivesFilters("pageSize", size);
    onPageChange(1, size);
  };

  const onStatusChange = (status) => {
    setObjectivesFilters("status", status);
    setObjectivesFilters("page", 1);
    fetchData({ status });
  };

  const onYearChange = (dueDate) => {
    setObjectivesFilters("dueDate", dueDate);
    setObjectivesFilters("page", 1);
    fetchData({ dueDate });
  };

  const searchObj = {
    dueDate: objectivesFilters.dueDate,
    status: objectivesFilters.status,
    limit: objectivesFilters.pageSize,
  };

  const clearFilters = () => {
    setObjectivesFilters("dueDate", "");
    setObjectivesFilters("status", "");
    setObjectivesFilters("page", 1);
    fetchData({});
  };

  useEffect(() => {
    fetchData(searchObj);
  }, [
    objectivesFilters.dueDate,
    objectivesFilters.status,
    objectivesFilters.page,
    objectivesFilters.pageSize,
  ]);

  useEffect(() => {
    fetchData(searchObj);
  }, [type]);

  const tableData = [];

  const makeTableData = () => {
    objectives?.data?.forEach((element) => {
      const dueDateChecked =
        element.dueDate === null ? "" : format(new Date(element.dueDate), "dd/MM/yyyy");
      const tableElement = {
        DateAdded: format(new Date(element.createdAt), "dd/MM/yyyy"),
        ObjectiveName: element.name,
        DueDate: dueDateChecked,
        LastEdited: format(new Date(element.updatedAt), "dd/MM/yyyy"),
        Status: element.status.charAt(0).toUpperCase() + element.status.slice(1),
        Id: element._id,
      };
      tableData.push(tableElement);
    });
    return tableData;
  };

  const noDataFor = type === "PDP" ? "development goal" : "objective";
  const noDataMsg =
    type === "PDP"
      ? `To write a ${noDataFor} for your PDP, please select the ‘add new ${noDataFor}’ button.`
      : `To write an ${noDataFor}, please select the ‘add new ${noDataFor}’ button.`;

  const getNoDataMessage = () => {
    if (objectivesFilters.dueDate !== "" || objectivesFilters.status !== "") {
      return "No results found";
    }
    if (isLineManagerView) {
      return `This employee has not yet set any ${noDataFor}s.`;
    }
    return `You currently have no ${noDataFor}s set. ${noDataMsg}`;
  };

  const headings = [
    "Date Added",
    `${type === "PDP" ? "Development Goal" : "Objective"} Name`,
    "Due Date",
    "Last Edited",
    "Status",
  ];

  const toViewObjective = (id, status) => {
    let dest;
    if (status === "Draft") {
      dest = `/edit-${actionFor}/${id}`;
    } else if (isLineManagerView) {
      dest = `/my-team/${employeeEmail}/view-${actionFor}/${id}`;
    } else {
      dest = `/view-${actionFor}/${id}`;
    }
    history.push(dest);
  };

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column" }}>
        <Loading loading={loading} />
        {(type === "PDP" ? !pdpsLoading : !objectivesLoading) && !loading && (
          <>
            <div className={styles.row_filters}>
              <Filters
                clearFilters={clearFilters}
                onStatusChange={onStatusChange}
                onYearChange={onYearChange}
                yearValue={objectivesFilters.dueDate}
                statusValue={objectivesFilters.status}
                isLineManagerView={isLineManagerView}
              />
            </div>
            <div className={`${styles.row_main} ${styles.objectives_table_row}`}>
              <TableView
                colHeadings={headings}
                tableData={makeTableData()}
                noDataMsg={getNoDataMessage()}
                onDeleteDraft={openDeleteDraftModal}
                onViewObjective={(Id, Status) => {
                  toViewObjective(Id, Status);
                }}
                isLineManagerView={isLineManagerView}
              />
            </div>
            <div className={styles.row_pagination}>
              {objectives && (
                <Pagination
                  onChange={onPageChange}
                  currentPage={objectivesFilters.page}
                  total={objectives?.total}
                  pageSize={objectivesFilters.pageSize}
                  onPageSizeChange={onPageSizeChange}
                />
              )}
            </div>
          </>
        )}
      </div>
      <AreYouSureModal
        show={isDeleteDraftModalOpen}
        close={closeDeleteDraftModal}
        confirm={() => fetchData(searchObj)}
        selectedId={selectedItemId}
        action="Delete"
        type={type}
      />
    </>
  );
};

export default ObjectivesContent;
